import { useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Badge, Button, Card, CardBody, EmptyState, Spinner } from '@fluxora/ui';

import { isNotFound } from '../api/bff-client';
import { usePlanDetailQuery } from '../api/plans.queries';
import { FunnelBackLink } from '../components/FunnelBackLink';
import { EflDisclosure } from '../components/EflDisclosure';
import { namedPlanComponents, planRateTypeLabelKey } from '../services/plan-language.service';
import { tduDisplayName } from '../services/zone.service';
import { PlanPriceHeadline } from '../components/PlanPriceHeadline';
import { SourceBadge } from '../components/SourceBadge';
import { useDeepLinkSource } from '../hooks/useDeepLinkSource';
import { useUsageSelection } from '../hooks/useUsageSelection';
import { DEFAULT_REFERENCE_USAGE } from '../schemas/shop.schema';
import { isDeepLink } from '../services/deep-link.service';
import { buildPlanStepPath, buildShelfPath } from '../services/funnel-rehydrate.service';
import {
  formatCentsPerKwh,
  formatCentsToDollars,
  formatRenewablePct,
} from '../services/price.service';
import { useFunnel } from '../state/FunnelProvider';
import { env } from '../../../config/env';

/**
 * Plan detail (E-SF02).
 *
 * AC1: a deep link (`/plan/:slug?src=ptc`) lands on this page and renders the
 * plan card + a single address field — no plan picker, no re-choosing. The
 * `slug` is the tariff id.
 *
 * AC2: the EFL/TOS/YRAC disclosure is gated by `env.featureEflPublish`
 * (`VITE_FEATURE_EFL_PUBLISH`, default false) → rendered as an explicit PREVIEW.
 *
 * ── THE HEADLINE PRICES AT THE SHOPPER'S OWN USAGE ─────────────────────────
 * The summary card used to headline the §25.475 midpoint constant (1000 kWh) no
 * matter what the shopper had chosen, so a meter resolved at 1400 kWh/mo saw one
 * price on the shelf card and a different one on the plan it opened. The usage now
 * comes from `FunnelProvider` (seeded from `?usage=`/`?tier=`, which every funnel
 * link carries), and the 1000 kWh constant survives only as the fallback for a bare
 * PTC deep link — where the shopper genuinely has not chosen a usage yet, and the
 * label ("at 1000 kWh/mo") discloses which figure they are being shown.
 *
 * All five async states are handled: loading / error / not-found (404) / success;
 * "empty" does not apply to a single resource.
 */
export function PlanDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const funnel = useFunnel();
  const selection = useUsageSelection();
  // Reads `?src=` AND remembers it for the rest of the visit, so the attribution
  // reaches `GET /bff/session` (and from there the Accounts prospect) instead of
  // dying with this page's URL. See useDeepLinkSource.
  const source = useDeepLinkSource();
  const deepLink = isDeepLink(source);

  const query = usePlanDetailQuery(slug);

  // SHOP-6 (partial): the meter/address is already resolved (meter step →
  // FunnelProvider), so Continue advances STRAIGHT to the credit/approval step —
  // no "Service address" step. The resolved candidate is carried from
  // FunnelProvider (as `?candidate=` for a reload-safe back-compat handle), and the
  // usage rides along so a refresh at the credit step — or Back out of it — still
  // knows what the shopper is being priced at. When no meter is resolved (e.g. a
  // bare PTC deep link), the credit guard sends the shopper to the landing chooser
  // to resolve their meter first.
  const goToCredit = (): void => {
    if (slug === undefined) return;
    navigate(
      buildPlanStepPath({
        tariffId: slug,
        step: 'credit',
        selection,
        ...(funnel.candidateId === undefined ? {} : { candidateId: funnel.candidateId }),
      }),
    );
  };

  // Back to the meter-filtered shelf (SHOP-5A), carrying the usage so the shelf
  // re-prices at the same kWh instead of tripping its no-usage guard. If no meter
  // is resolved, `/shop/plans` guards back to the landing chooser — never the dead
  // `/plans`.
  const backLink = <FunnelBackLink to={buildShelfPath(selection)} label={t('shop.detail.back')} />;

  if (query.isPending) {
    return (
      <main className="mx-auto flex max-w-6xl justify-center px-4 py-16">
        <Spinner size="lg" label={t('shop.detail.loading')} />
      </main>
    );
  }

  if (query.isError) {
    if (isNotFound(query.error)) {
      return (
        <main className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-12">
          {backLink}
          <EmptyState
            title={t('shop.detail.notFound.title')}
            description={t('shop.detail.notFound.body')}
          />
        </main>
      );
    }
    return (
      <main className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-12">
        {backLink}
        <Alert variant="danger" title={t('shop.detail.error.title')} role="alert">
          {t('shop.detail.error.body')}
        </Alert>
      </main>
    );
  }

  const plan = query.data;

  const summary = (
    /* The same pastel-tint ring the funnel's "you are here" surfaces wear. */
    <div className="rounded-[calc(var(--r-lg)+1.5px)] bg-[image:var(--brand-gradient-tint)] p-[1.5px] shadow-card">
      <Card variant="glass" padding="md" className="!rounded-[var(--r-lg)]">
        <CardBody className="flex flex-col items-center gap-3 p-0 py-2 text-center">
          <Badge variant="primary">{t(planRateTypeLabelKey(plan))}</Badge>
          <div className="flex flex-col gap-0.5">
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {plan.planName}
            </h1>
            <span className="text-sm text-foreground/60">{plan.supplierName}</span>
          </div>
          <PlanPriceHeadline
            planId={plan.tariffId}
            monthlyKwh={funnel.monthlyUsageKwh ?? DEFAULT_REFERENCE_USAGE}
            align="center"
          />
          <div className="grid w-full grid-cols-2 gap-2 pt-1 sm:grid-cols-4">
            <Stat
              label={t('shop.detail.term')}
              value={
                plan.termMonths > 0
                  ? t('shop.detail.months', { months: plan.termMonths })
                  : t('shop.card.term.none')
              }
            />
            <Stat label={t('shop.detail.renewable')} value={formatRenewablePct(plan.recPctMin)} />
            <Stat label={t('shop.detail.tdsp')} value={tduDisplayName(plan.tdsp)} />
            <Stat label={t('shop.detail.etf')} value={formatCentsToDollars(plan.etfCents)} />
          </div>
        </CardBody>
      </Card>
    </div>
  );

  // AC1 — deep-link landing: plan card + address input as the only field.
  if (deepLink) {
    return (
      <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10">
        <div className="flex items-center justify-between gap-2">
          {backLink}
          <SourceBadge source={source} />
        </div>
        <h2 className="font-display text-center text-xl font-bold tracking-tight text-foreground">
          {t('shop.address.deepLink.heading')}
        </h2>
        {summary}
        <div className="flex justify-center">
          <Button variant="primary" size="lg" shape="pill" className="cta-sheen" onClick={goToCredit}>
            {t('shop.detail.continueToCredit')}
          </Button>
        </div>
      </main>
    );
  }

  // Organic detail — full disclosure, compact: facts as a tile row, the two
  // pricing panels side by side, the EFL reading pane full-width below.
  return (
    <main className="anim-stagger mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10">
      {backLink}
      {summary}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
      <Card variant="glass" padding="md" className="flex flex-col gap-2">
        <CardBody className="flex flex-col gap-2 p-0">
          <h3 className="font-display text-lg font-bold tracking-tight text-foreground">
            {t('shop.detail.energyChargeHeading')}
          </h3>
          <span className="text-2xl font-bold tabular-nums text-foreground">
            {formatCentsPerKwh(plan.rateCentsPerKwh)}
            <span className="ml-1 text-sm font-normal text-foreground/60">
              {t('shop.card.avgPrice.unit')}
            </span>
          </span>
          <span className="text-xs text-foreground/60">{t('shop.detail.energyChargeNote')}</span>
        </CardBody>
      </Card>

      {plan.components.length > 0 ? (
        <Card variant="glass" padding="md" className="flex flex-col gap-2">
          <CardBody className="flex flex-col gap-2 p-0">
            <h3 className="font-display text-lg font-bold tracking-tight text-foreground">
              {t('shop.detail.components.title')}
            </h3>
            <ul className="flex flex-col divide-y divide-border/60">
              {/*
                E-SF19 P2 — the customer's words, not the catalogue's. These lines
                used to print REP_ENERGY / TDU_PASSTHROUGH verbatim. A component
                this build cannot name is DROPPED by `namedPlanComponents` rather
                than printed as its code: an incomplete breakdown is honest, an
                unreadable one is not, and the headline average price already
                accounts for every component.
              */}
              {namedPlanComponents(plan.components).map(({ component, labelKey }) => (
                <li
                  key={`${component.code}-${component.effectiveFrom}`}
                  className="flex justify-between py-1.5 text-sm text-foreground/80"
                >
                  <span>{t(labelKey)}</span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatCentsPerKwh(component.rateCentsPerKwh)}
                    <span className="ml-1 font-normal text-foreground/60">
                      {t('shop.card.avgPrice.unit')}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
      </div>

      <EflDisclosure
        eflText={plan.eflText}
        tosText={plan.tosText}
        yracText={plan.yracText}
        publishEnabled={env.featureEflPublish}
      />

      <div className="flex justify-center">
        <Button variant="primary" size="lg" shape="pill" className="cta-sheen" onClick={goToCredit}>
          {t('shop.detail.continueToCredit')}
        </Button>
      </div>
    </main>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-0.5 rounded-[14px] border border-border/70 bg-bg px-3 py-3 text-center">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-foreground/50">
        {label}
      </span>
      <span className="truncate text-sm font-bold text-foreground">{value}</span>
    </div>
  );
}
